import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import OracyLogo from '@/components/OracyLogo';
import { motion } from 'framer-motion';
import { GraduationCap, Users, BookOpen, Loader2 } from 'lucide-react';
import { OnboardingStep1Request } from '@/lib/api/auth';

interface OnboardingStep1Props {
  onNext: (data: OnboardingStep1Request) => Promise<void>;
  isLoading: boolean;
}

const userTypes = [
  {
    value: 'student' as const,
    label: 'Student',
    description: 'I am a learner using Oracy to improve my oracy skills',
    icon: GraduationCap,
  },
  {
    value: 'parent' as const,
    label: 'Parent/Guardian',
    description: "I am a parent registering my child's account",
    icon: Users,
  },
  {
    value: 'teacher' as const,
    label: 'Teacher',
    description: 'I am an educator using Oracy to assess and support students',
    icon: BookOpen,
  },
];

export default function OnboardingStep1UserType({ onNext, isLoading }: OnboardingStep1Props) {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<'student' | 'parent' | 'teacher' | null>(null);
  const [error, setError] = useState('');

  const handleContinue = async () => {
    if (!selectedType) {
      setError('Please select a user type to continue.');
      return;
    }
    setError('');

    try {
      await onNext({ user_type: selectedType });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <OracyLogo variant="full" theme="light" />
        </div>

        <Card className="w-full shadow-xl">
          <CardHeader className="space-y-1 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">1</span>
              <span className="text-sm text-muted-foreground">of 3</span>
            </div>
            <CardTitle className="text-2xl font-bold">How will you use Oracy?</CardTitle>
            <CardDescription>
              Choose the option that best describes you
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Error Message */}
            {error && (
              <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950/20 rounded-lg">
                {error}
              </div>
            )}

            {/* User Type Options */}
            <div className="space-y-3">
              {userTypes.map((type) => {
                const Icon = type.icon;
                const isSelected = selectedType === type.value;
                
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setSelectedType(type.value)}
                    disabled={isLoading}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold">{type.label}</p>
                        <p className="text-sm text-muted-foreground">{type.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <Button
              onClick={handleContinue}
              className="w-full"
              disabled={!selectedType || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Continuing...
                </>
              ) : (
                'Continue'
              )}
            </Button>
            
            <p className="text-sm text-center text-muted-foreground">
              Already have an account?{' '}
              <button 
                onClick={() => navigate('/login')}
                className="text-primary hover:underline"
              >
                Sign in
              </button>
            </p>
          </CardFooter>
        </Card>
      </motion.div>
    </div>
  );
}
